// THE RUN-DATA WATCH (owner vrequest 2026-09-16, CR-20260916-004).
//
// The changed-since-run cues (maven-setup-param-diff.ts) compare the Setup FIELDS against what
// the run was sent with. The two things the run actually prices — the portfolio's saved
// positions and its saved hedge book — never enter that comparison: Maven sends ids only and the
// backend reads both. So a leg edited in the APG, or a hedge added in the Hedging Grid, left the
// cards looking current. Package Comparison already shows the yellow "portfolio changed" cue for
// exactly this; the owner wants the same awareness in the Maven Setup, as one pinned stripe.
//
// HOW IT KNOWS. Every stored user setting comes back from the shell on the event stream
// (`UserSettingsChangedDto` → `UserSettingsService.userSettingChanged$`), the local user's own
// saves included, and the two data sets live under known keys:
//   apg.saved-positions.<pfId>.<underlying>.<strategy>   — the APG positions (one key)
//   hg.hedge-positions.<pfId>.<underlying>               — the hedge book, base underlying
//   hg.hedge-positions.<pfId>.others                     — the hedge book, other underlyings (ES)
// At Apply the values under those keys are snapshotted as JSON (the BASELINE); every echo for
// one of the keys is compared against it. The comparison is per KEY, on the ECHOED value, and
// never on a re-read: for a portfolio owned by ANOTHER trader the services read the login-time
// copy (`UserSettingsService` keeps other users' live values on a separate index), so a re-read
// would miss exactly the change a super user needs to see. Package Comparison compares the same
// way (`checkPortfolioDataChanges`).
//
// PURE FUNCTIONS, no Angular. The component owns the subscription and the baseline lifecycle.

export type MavenRunDataKind = 'positions' | 'hedges';

/** What the run was sent on: for each watched key, the value as JSON. */
export interface MavenRunDataBaseline {
    /** portfolio owner — an echo for another user's key is not this portfolio's */
    userId: string;
    /** key → kind, for every key watched */
    kinds: { [key: string]: MavenRunDataKind };
    /** key → normalised JSON of the value at Apply */
    json: { [key: string]: string };
}

/** Which data sets differ from the baseline right now. */
export interface MavenRunDataChanged {
    positions: boolean;
    hedges: boolean;
}

export const NO_DATA_CHANGE: MavenRunDataChanged = {positions: false, hedges: false};

// Absent and empty are the same book: a deleted "others" key echoes `null`, a cleared one stores
// `[]`, and neither is a change from the other.
export function normalizeRunDataJson(value: any): string {
    return JSON.stringify(value === null || value === undefined ? [] : value);
}

export function makeRunDataBaseline(
    userId: string,
    entries: { key: string; kind: MavenRunDataKind; value: any }[]
): MavenRunDataBaseline {
    const baseline: MavenRunDataBaseline = {userId, kinds: {}, json: {}};
    entries.forEach(e => {
        baseline.kinds[e.key] = e.kind;
        baseline.json[e.key] = normalizeRunDataJson(e.value);
    });
    return baseline;
}

/**
 * One settings echo against the baseline. Returns the kind the echo touched and whether it now
 * differs, or null when the echo is not one of this run's keys (another user, another key).
 * The caller keeps a per-KEY live map so a base-key echo and an others-key echo each hold their
 * own verdict; `applyRunDataEcho` below does that bookkeeping.
 */
export function classifyRunDataEcho(
    baseline: MavenRunDataBaseline | null,
    echo: { userId: string; key: string; value: any }
): { kind: MavenRunDataKind; differs: boolean } | null {
    if (!baseline || echo.userId !== baseline.userId) {
        return null;
    }
    const kind = baseline.kinds[echo.key];
    if (!kind) {
        return null;
    }
    return {kind, differs: normalizeRunDataJson(echo.value) !== baseline.json[echo.key]};
}

/**
 * The bookkeeping: `liveDiffers` holds, per key, whether the LAST echo for it differed from the
 * baseline (a key never echoed since Apply is unchanged). A kind is changed while ANY of its keys
 * differs — the hedge book is two keys, and putting the base key back does not put "others" back.
 */
export function applyRunDataEcho(
    baseline: MavenRunDataBaseline | null,
    liveDiffers: { [key: string]: boolean },
    echo: { userId: string; key: string; value: any }
): MavenRunDataChanged | null {
    const verdict = classifyRunDataEcho(baseline, echo);
    if (!verdict) {
        return null;
    }
    liveDiffers[echo.key] = verdict.differs;
    return summarizeRunDataChanged(baseline, liveDiffers);
}

export function summarizeRunDataChanged(
    baseline: MavenRunDataBaseline,
    liveDiffers: { [key: string]: boolean }
): MavenRunDataChanged {
    const changed: MavenRunDataChanged = {positions: false, hedges: false};
    Object.keys(baseline.kinds).forEach(key => {
        if (liveDiffers[key]) {
            changed[baseline.kinds[key]] = true;
        }
    });
    return changed;
}

/**
 * The stripe's sentence (owner wording, capitalised words), or null when nothing changed.
 * `zoneChanged` is the Current Zone moved since the run — a manual "set as current zone" from the
 * footer, or a re-read (owner, 2026-09-16, on the first build: "same detection for manual current
 * zone change"). One line always: the changed items joined, "and" before the last.
 *   "Portfolio Positions Changed" · "Hedges and Current Zone Changed" ·
 *   "Portfolio Positions, Hedges and Current Zone Changed"
 */
export function runDataChangedSentence(changed: MavenRunDataChanged | null, zoneChanged = false): string | null {
    const parts: string[] = [];
    if (changed && changed.positions) {
        parts.push('Portfolio Positions');
    }
    if (changed && changed.hedges) {
        parts.push('Hedges');
    }
    if (zoneChanged) {
        parts.push('Current Zone');
    }
    if (parts.length === 0) {
        return null;
    }
    const subject = parts.length === 1
        ? parts[0]
        : parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
    return subject + ' Changed';
}
